import YatungConfirmAlert from '@Src/_basic/components/YatungConfirmAlert';
import YatungPage from '@Src/_basic/components/YatungPage';
import { THIRD_PARTY_SOURCE_KIND, ThirdPartyAccountFormValues } from '@Src/_basic/object/ThirdPartyAccountType';
import { ThirdPartyAccountApi } from '@Src/_basic/protocol/accountRights/thirdPartyAccount/ThirdPartyAccountApi';
import { Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import EditAccountField from '../../../_components/EditAccountField';
import EditAccountLabel from '../../../_components/EditAccountLabel';
import ThirdPartyAccountFormActions from '../_components/ThirdPartyAccountFormActions';
import ThirdPartyAccountSearchLimitFields, {
  toSearchLimitRequest,
} from '../_components/ThirdPartyAccountSearchLimitFields';
import { createThirdPartyAccountSchema } from '../_components/thirdPartyAccountSchema';

const initialValues: ThirdPartyAccountFormValues = {
  account: '',
  accountName: '',
  description: '',
  availableDays: '',
  searchLimitDays: '0',
  searchStartDate: null,
  searchEndDate: null,
};

export default function ThirdPartyAccountCreateCard() {
  const { t: i18T } = useTranslation();
  const navigate = useNavigate();

  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>('');
  // 帳號已建立但限制搜尋資料日期儲存失敗時，關閉提示後仍導向編輯頁
  const [createdId, setCreatedId] = useState<string>('');

  const validationSchema = useMemo(() => createThirdPartyAccountSchema(i18T), [i18T]);

  const handleGoBack = () => {
    navigate('/UsersManagePage/ThirdPartyAccountManagement');
  };

  const navigateToUpdate = (accountId: string) => {
    navigate(`/UsersManagePage/ThirdPartyAccountManagement/ThirdPartyAccountUpdate/${accountId}`, {
      state: accountId,
    });
  };

  const handleCloseAlert = () => {
    setIsShowAlert(false);
    if (createdId) navigateToUpdate(createdId);
  };

  const handleSubmit = (values: ThirdPartyAccountFormValues) => {
    ThirdPartyAccountApi.createAccount(
      {
        account: values.account,
        accountName: values.accountName,
        sourceKind: THIRD_PARTY_SOURCE_KIND,
        state: '1',
        description: values.description,
        extraData: {
          availableDays: Number(values.availableDays),
        },
      },
      (_data) => {
        // 帳號建立成功後, 再以 POST 儲存限制搜尋資料日期
        ThirdPartyAccountApi.createAccountSearchLimit(
          toSearchLimitRequest(values),
          () => navigateToUpdate(_data.id),
          (res) => {
            setCreatedId(_data.id);
            setAlertText(
              res?.message ?? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.SEARCH_LIMIT_UPDATE_FAILED'),
            );
            setIsShowAlert(true);
          },
        );
      },
      (res) => {
        setAlertText(res?.message ?? i18T('GLOBAL.ERROR', { text: i18T('GLOBAL.SAVE') }));
        setIsShowAlert(true);
      },
    );
  };

  return (
    <>
      <YatungPage
        title={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.CREATE_ROUTE_TITLE')}
        contentBgColor="#FFF"
        body={
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {(formik) => (
              <Form>
                <Stack width="100%">
                  <Grid container item xs={12} justifyContent={'space-between'}>
                    <Grid container item xs={true}></Grid>
                    <Grid container item xs={true} gap={4} justifyContent={'center'} sx={{ mr: 3, my: 1 }}>
                      <ThirdPartyAccountFormActions onSubmit={formik.handleSubmit} onGoBack={handleGoBack} />
                    </Grid>
                  </Grid>
                  <Stack>
                    <Stack direction="row" sx={{ mt: 3 }}>
                      <EditAccountLabel
                        label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ACCOUNT')}
                        isStar={true}
                      />
                      <EditAccountField target="account" />
                    </Stack>
                    <Stack direction="row" sx={{ mt: 3 }}>
                      <EditAccountLabel
                        label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ACCOUNT_NAME')}
                        isStar={true}
                      />
                      <EditAccountField target="accountName" />
                    </Stack>
                    <Stack direction="row" sx={{ mt: 3 }}>
                      <EditAccountLabel label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.DESCRIPTION')} />
                      <EditAccountField target="description" />
                    </Stack>
                    <Stack direction="row" sx={{ mt: 3 }}>
                      <EditAccountLabel
                        label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.AVAILABLE_DAYS')}
                        isStar={true}
                      />
                      <EditAccountField target="availableDays" />
                    </Stack>
                    <ThirdPartyAccountSearchLimitFields />
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        }
      />
      <YatungConfirmAlert
        headerText={i18T('GLOBAL.SAVE')}
        isShowAlert={isShowAlert}
        onClose={handleCloseAlert}
        alertText={alertText}
      />
    </>
  );
}
